import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LOGO_URL } from "../lib/brand";
import { Menu, X, LogIn } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useT } from "../i18n/LanguageContext";
import { track } from "../lib/analytics";

export const Navbar = () => {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { href: "#konzept", label: t.nav.konzept },
    { href: "#ablauf", label: t.nav.ablauf },
    { href: "#track-record", label: t.nav.track },
    { href: "#konditionen", label: t.nav.konditionen },
    { href: "#faq", label: t.nav.faq },
    { href: "/blog", label: "Blog", isInternal: true },
    { href: "/about", label: "Über uns", isInternal: true },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[var(--al-line)]" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
          <span className="font-display font-semibold text-[15px] tracking-tight">
            <span className="al-grad-text">ALPHALANE</span>
            <span className="text-slate-500 ml-2">AI</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) =>
            l.isInternal ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.href.replace(/\//g, "")}`}
                className="text-[14px] text-slate-600 hover:text-slate-900 transition-colors font-medium"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-[14px] text-slate-600 hover:text-slate-900 transition-colors font-medium"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <Link
            to="/dashboard"
            data-testid="nav-login-desktop"
            className="inline-flex items-center gap-1.5 text-[14px] font-display font-semibold text-slate-600 hover:text-slate-900 transition-colors px-3 py-2 rounded-full border border-[var(--al-line)] bg-white"
          >
            <LogIn className="h-4 w-4" /> Login
          </Link>
          <a href="#kontakt" data-testid="nav-cta-contact" onClick={() => track("cta_click", { meta: { id: "nav_desktop" } })} className="al-cta !py-2.5 !px-5 text-sm">
            {t.nav.cta}
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <Link
            to="/dashboard"
            data-testid="nav-login-mobile"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-[var(--al-line)] bg-white text-[13px] font-display font-semibold text-slate-700"
          >
            <LogIn className="h-4 w-4" /> Login
          </Link>
          <button
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
            className="h-10 w-10 rounded-full border border-[var(--al-line)] flex items-center justify-center bg-white"
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="lg:hidden border-t border-[var(--al-line)] bg-white">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) =>
              l.isInternal ? (
                <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className="text-slate-700 py-1.5 font-medium">
                  {l.label}
                </Link>
              ) : (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-slate-700 py-1.5 font-medium">
                  {l.label}
                </a>
              )
            )}
            <div className="flex items-center justify-between gap-3 pt-2">
              <LanguageSwitcher />
              <a href="#kontakt" onClick={() => { track("cta_click", { meta: { id: "nav_mobile" } }); setOpen(false); }} className="al-cta">
                {t.nav.cta}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
